"use client";

import React from 'react';
import { Row, Col, Button, Card, Spinner, Alert } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

// Components
import ProductImage from './ProductImage';
import ProductQuantity from './ProductQuantity';
import ProductInfo from './ProductInfo';
import ProductMeta from './ProductMeta';

// Custom hooks
import { useProductDetails } from '../hooks/use-product-details';
import { FILES_BASE_URL } from '../../../config';
import { Product } from '../../../store/cart-store';
import ProductDescription from './ProductDescription';


// Type assertion function to safely access product properties
const getProductData = (product: Product | null | undefined) => {
  if (!product) return null;

  // Cast to any to safely access properties that might not be in the Product interface
  const p = product as any;

  return {
    ...product,
    description: p.description || '',
    category: p.category || { name: 'Productos', id: '' },
    aditionalData: p.aditionalData || [],
    images: p.images || []
  };
};

interface ProductDetailsContentProps {
  productId?: string;
}

const ProductDetailsContent: React.FC<ProductDetailsContentProps> = ({ productId: propProductId }) => {
  // Get productId from URL params if not provided as prop
  const { id: urlProductId } = useParams<{ id: string }>();
  const productId = propProductId || urlProductId || '';

  const {
    product: rawProduct,
    isLoading,
    error,
    quantity,
    handleQuantityChange,
    handleAddToCart,
    handleBuyNow
  } = useProductDetails(productId);

  // Process product data safely
  const product = getProductData(rawProduct);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando detalles del producto...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <Alert variant="danger">
        {error instanceof Error ? error.message : "Producto no encontrado"}
      </Alert>
    );
  }

  // Render the product details
  return (
    <>
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4">
          <Row>
            <Col lg={5}>
              {/* Product Image */}
              <ProductImage
                images={[...product.images.map((image: any) => `${FILES_BASE_URL}/${image}`)]}
              />
            </Col>
            <Col lg={7}>
              <div className="product-details">
                {/* Product Title and Description */}
                <ProductInfo
                  title={product.title}
                  description={product.shortDescription}
                />

                {/* Product Price */}
                <div className="product-price mb-4">
                  {product.aditionalData && product.aditionalData.length > 0 && (
                    <>
                      <h4 className="fw-medium fs-20">
                        ${product.aditionalData[0].price_local_currency?.toLocaleString()} COP
                      </h4>
                      {/* <span className="text-muted small">
                        ${product.aditionalData[0].price_usdt?.toFixed(2)} USDT
                      </span> */}
                    </>
                  )}
                </div>

                {/* Product Quantity and Add to Cart */}
                <div className="d-flex align-items-center gap-3 mb-4">
                  <ProductQuantity
                    quantity={quantity}
                    onChange={handleQuantityChange}
                  />
                  <Button
                    variant="primary"
                    className="px-4 py-2"
                    onClick={handleAddToCart}
                  >
                    <i className="ri-shopping-cart-line me-2"></i>
                    Agregar al carrito
                  </Button>
                 <Button
                    variant="warning"
                    className="px-4 py-2 text-dark fw-bold"
                    onClick={handleBuyNow}
                  >
                    <i className="ri-flashlight-line me-2"></i>
                    Comprar ahora
                  </Button>
                </div>

                {/* Product Meta Information */}
                <ProductMeta
                  sku={product._id || 'N/A'}
                  category={product.category}
                  aditionalData={product.aditionalData[0]}
                />

                {/* Product Benefits */}
                {/* <div className="product-benefits my-4">
                  <h5 className="fs-16 mb-3">Beneficios:</h5>
                  <ul className="ps-3">
                    <li className="mb-2">Envío gratis en pedidos superiores a $50</li>
                    <li className="mb-2">Garantía de devolución de 30 días</li>
                    <li>Soporte al cliente 24/7</li>
                  </ul>
                </div> */}


                {/* Delivery Information */}
                {product.aditionalData && product.aditionalData.length > 0 && (
                  <div className="delivery-info border-top pt-3 mt-3">
                    <h5 className="fs-16 mb-3">Información de entrega:</h5>
                    <div className="d-flex flex-wrap gap-3">
                      <span className="badge bg-light text-black p-2">
                        <i className="ri-time-line me-1 text-black"></i>
                        Tiempo de entrega: {product.aditionalData[0].delivery_time_days || 0} días
                      </span>

                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <div className="mt-4">
        <ProductDescription product={product} />
      </div>
    </>
  );
};


export default ProductDetailsContent;
